import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  company: "",
  full_name: "",
  email: "",
  phone: "",
  service_type: "",
  surface: "",
  frequency: "",
  address: "",
  message: "",
};

export default function QuoteForm() {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.full_name || !data.email || !data.phone || !data.service_type) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, data);
      toast.success("Demande envoyée. Nous vous recontactons sous 48h.");
      setData(initial);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="section-pad bg-card/30 border-y border-border">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-6">Demande de devis</div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1]">
            Recevez votre<br />
            <span className="italic text-[hsl(var(--accent))]">proposition</span><br />
            sous 48 heures.
          </h2>
          <p className="mt-8 text-muted-foreground leading-relaxed max-w-md">
            Un devis clair, sans surprise. Nous étudions chaque demande individuellement pour vous
            proposer une solution adaptée à votre site et à votre budget.
          </p>
          <ul className="mt-10 space-y-3 text-sm">
            {["Étude personnalisée", "Visite gratuite sur site", "Tarification transparente", "Sans engagement"].map(
              (b) => (
                <li key={b} className="flex items-center gap-3 text-foreground/80">
                  <span className="font-serif text-[hsl(var(--primary))] text-base">✓</span>
                  {b}
                </li>
              )
            )}
          </ul>
        </div>

        <form
          onSubmit={submit}
          data-testid="quote-form"
          className="lg:col-span-7 border border-border rounded-sm p-8 lg:p-10 bg-background space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Société (optionnel)">
              <Input
                data-testid="quote-company"
                value={data.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder="Nom de la société"
              />
            </Field>
            <Field label="Nom complet *">
              <Input
                data-testid="quote-fullname"
                value={data.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                placeholder="Jean Dupont"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Email *">
              <Input
                data-testid="quote-email"
                type="email"
                value={data.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="vous@email.com"
              />
            </Field>
            <Field label="Téléphone *">
              <Input
                data-testid="quote-phone"
                value={data.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+32 4XX XX XX XX"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Type de prestation *">
              <Select value={data.service_type} onValueChange={(v) => set("service_type", v)}>
                <SelectTrigger data-testid="quote-service-type">
                  <SelectValue placeholder="Choisir…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bureaux">Nettoyage de bureaux</SelectItem>
                  <SelectItem value="chantier">Nettoyage de chantier</SelectItem>
                  <SelectItem value="autre">Autre / Mixte</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Fréquence souhaitée">
              <Select value={data.frequency} onValueChange={(v) => set("frequency", v)}>
                <SelectTrigger data-testid="quote-frequency">
                  <SelectValue placeholder="Choisir…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ponctuel">Ponctuel / unique</SelectItem>
                  <SelectItem value="hebdomadaire">Hebdomadaire</SelectItem>
                  <SelectItem value="bi-hebdomadaire">2× / semaine</SelectItem>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="quotidien">Quotidien</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Surface approximative">
              <Input
                data-testid="quote-surface"
                value={data.surface}
                onChange={(e) => set("surface", e.target.value)}
                placeholder="ex : 250 m²"
              />
            </Field>
            <Field label="Adresse / ville">
              <Input
                data-testid="quote-address"
                value={data.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="Rue, code postal, ville"
              />
            </Field>
          </div>

          <Field label="Précisions (optionnel)">
            <Textarea
              data-testid="quote-message"
              value={data.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Décrivez votre besoin, contraintes d'horaires, accès…"
              rows={4}
            />
          </Field>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="quote-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Envoi en cours…" : "Envoyer ma demande"}
          </button>
          <p className="text-xs text-muted-foreground">* Champs obligatoires. Nous traitons vos données conformément au RGPD.</p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
